// Chemin: C:\gestion-copro\src\components\sentinelle\TransformerCourrierModal.tsx
// Description: Modal de suggestion de transformation d'un email en courrier formel
// Détecte les éléments engageants et propose un traitement plus formel
// Session 52 : création initiale - Spec SENTINELLE V2

import React from 'react';
import {
  FileText,
  Mail,
  ArrowRight,
  X,
  AlertCircle,
  Check,
} from 'lucide-react';

// ============================================================================
// TYPES
// ============================================================================

interface TransformerCourrierModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Mots-clés engageants détectés */
  motsClesDetectes: string[];
  /** Callback pour transformer en courrier formel */
  onTransformer: () => void;
  /** Callback pour rester en email */
  onResterEmail: () => void;
}

// ============================================================================
// COMPOSANT PRINCIPAL
// ============================================================================

const TransformerCourrierModal: React.FC<TransformerCourrierModalProps> = ({
  isOpen,
  onClose,
  motsClesDetectes,
  onTransformer,
  onResterEmail,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-lg bg-white rounded-xl shadow-2xl">
          {/* Header - Bleu pour suggestion */}
          <div className="bg-blue-600 text-white px-6 py-4 rounded-t-xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white/20 rounded-lg">
                  <FileText className="w-6 h-6" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold">Suggestion SENTINELLE</h2>
                  <p className="text-blue-100 text-sm">
                    Contenu formel détecté
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Contenu */}
          <div className="p-6">
            {/* Message d'explication */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-blue-800 font-medium">
                    Votre message contient des éléments formels
                  </p>
                  <p className="text-blue-700 text-sm mt-1">
                    Ce type de communication mériterait un traitement en courrier formel 
                    (numéroté, archivé, PDF possible).
                  </p>
                </div>
              </div>
            </div>

            {/* Mots-clés détectés */}
            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-700 mb-2">
                Éléments détectés :
              </h3>
              <div className="flex flex-wrap gap-2">
                {motsClesDetectes.map((mot, index) => (
                  <span 
                    key={index}
                    className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800"
                  >
                    {mot}
                  </span>
                ))}
              </div>
            </div>

            {/* Comparaison Email vs Courrier */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              {/* Email */}
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Mail className="w-5 h-5 text-gray-500" />
                  <span className="font-medium text-gray-700">Email</span>
                </div>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-start gap-2">
                    <span className="text-gray-400">•</span>
                    Envoi rapide
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-gray-400">•</span>
                    Pas de numérotation
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-gray-400">•</span>
                    Archivage optionnel
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-gray-400">•</span>
                    Ton souple
                  </li>
                </ul>
              </div>

              {/* Courrier */}
              <div className="border-2 border-purple-300 bg-purple-50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <FileText className="w-5 h-5 text-purple-600" />
                  <span className="font-medium text-purple-800">Courrier formel</span>
                  <span className="ml-auto text-xs bg-purple-200 text-purple-700 px-2 py-0.5 rounded-full">
                    Recommandé
                  </span>
                </div>
                <ul className="space-y-2 text-sm text-purple-700">
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-purple-500 mt-0.5" />
                    Numérotation (COU-2026-XXX)
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-purple-500 mt-0.5" />
                    PDF généré automatiquement
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-purple-500 mt-0.5" />
                    Archivage GED systématique
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-purple-500 mt-0.5" />
                    Vote CS intégré
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Footer avec actions */}
          <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 rounded-b-xl">
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={onResterEmail}
                className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
              >
                <Mail className="w-4 h-4" />
                Rester en email
              </button>
              <button
                onClick={onTransformer}
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-colors"
              >
                <ArrowRight className="w-4 h-4" />
                Transformer en courrier formel
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TransformerCourrierModal;
